import { fetchGetOnlineAccount } from '@iwoca/lapi-client/edge';

import { getAccountNumber } from './getAccountNumber';

export type OpenBankingBank = {
    bank_id: string;
    bank_name: string;
    logo_url: string;
};

export type LinkedAccount = {
    /** Subaccount id — display and React keys only. Not a valid API path segment. */
    accountId: string;
    /** UUID of the connection this subaccount belongs to. Required by /online_account/. */
    onlineAccountId: string;
    bankName?: string;
    logoUrl?: string;
    accountNumberMasked?: string;
};

export const findBankByName = (
    provider: string | null,
    banks: OpenBankingBank[]
) => {
    if (!provider) return undefined;
    const normalised = provider.toLowerCase();
    return banks.find((bank) => bank.bank_name.toLowerCase() === normalised);
};

export const getLinkedAccounts = (
    response: Awaited<ReturnType<typeof fetchGetOnlineAccount>> | undefined,
    banks: OpenBankingBank[] = []
): LinkedAccount[] => {
    if (!response?.data) return [];

    const linkedAccountsByKey = response.data
        .filter((entry) => entry.status?.code === 'active')
        .flatMap((entry) => {
            const bank = findBankByName(entry.provider, banks);
            return entry.accounts.map((account) => {
                const accountNumber = getAccountNumber(account.extra);
                const key = accountNumber
                    ? `${entry.provider ?? ''}:${accountNumber}`
                    : `id:${account.account_id}`;
                return [
                    key,
                    {
                        accountId: String(account.account_id),
                        onlineAccountId: entry.online_account_id,
                        bankName: entry.provider ?? undefined,
                        logoUrl: bank?.logo_url,
                        accountNumberMasked: accountNumber?.slice(-4)
                    }
                ] as const;
            });
        });

    return Array.from(new Map(linkedAccountsByKey).values());
};
